import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';

import logo from 'res/AthletesLaunch_Logo.png';
import { useCurrentUser } from 'providers/user/UserContext';
import { useFirebase } from 'providers/firebase/FirebaseContext';
import useFeatureAccess from 'utils/useFeatureAccess';
import {
    VaultIcon, MatrixIcon, RocketIcon, TrustIcon,
    ExploreIcon, SponsorshipsIcon, ConnectionsIcon, MarketplaceIcon,
    BlastIcon,
} from 'components/shared/ModuleIcons';

/* ─── Inline SVG Icons ─── */
const ChevronDown = ({ size = 14, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={style}>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

function MessagesIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
}
function CompaniesIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>);
}
function BeaconIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /><line x1="12" y1="8" x2="12" y2="16" strokeWidth="2.5" /><line x1="8" y1="12" x2="16" y2="12" strokeWidth="2.5" /></svg>);
}
function NotificationsIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>);
}
function AnalyticsIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>);
}
// eslint-disable-next-line no-unused-vars
function SocialFeedIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>);
}
function TwinStudioIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 15c6.667-6 13.333 0 20-6" /><path d="M9 22c1.798-1.998 2.518-3.995 2.807-5.993" /><path d="M15 2c-1.798 1.998-2.518 3.995-2.807 5.993" /><path d="M2 9c6.667 6 13.333 0 20 6" /></svg>);
}
// eslint-disable-next-line no-unused-vars
function LiveStreamIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" /><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4" /><circle cx="12" cy="12" r="2" /><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4" /><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" /></svg>);
}
function TicketsIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3z" /><path d="M13 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" /><path d="M20 12v7a2 2 0 0 1-2 2h-5" /></svg>);
}
function CalendarIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);
}
function LiveEventsIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill={color} stroke="none" /></svg>);
}
function ServiceMktIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>);
}
function CommerceIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h2l3 14h10l4-9H7" /><circle cx="10" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" /></svg>);
}
function OutreachIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.01z" /><line x1="14" y1="2" x2="14" y2="8" /><polyline points="17 5 14 2 11 5" /></svg>);
}
function TeamsCollectivesIcon({ size = 20, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><circle cx="12" cy="10" r="3" /><path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662" /></svg>);
}
function SearchIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
}
function LogoutIcon({ size = 18, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>);
}

/* ─── User Group Icons ─── */
function AthleteIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>);
}
function CompanyIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="9" y1="6" x2="9" y2="6.01" /><line x1="15" y1="6" x2="15" y2="6.01" /><line x1="9" y1="10" x2="9" y2="10.01" /><line x1="15" y1="10" x2="15" y2="10.01" /><line x1="9" y1="14" x2="9" y2="14.01" /><line x1="15" y1="14" x2="15" y2="14.01" /><path d="M9 18h6" /></svg>);
}
function CollectiveIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3" /><circle cx="17" cy="7" r="3" /><path d="M13 21v-4a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v4" /><path d="M23 21v-4a3 3 0 0 0-2-2.83" /></svg>);
}
function AgencyIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><line x1="9" y1="10" x2="15" y2="10" /><line x1="9" y1="14" x2="13" y2="14" /></svg>);
}
function TrainerIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 5v14" /><path d="M18 5v14" /><path d="M6 9h12" /><circle cx="3" cy="5" r="2" /><circle cx="21" cy="5" r="2" /><circle cx="3" cy="19" r="2" /><circle cx="21" cy="19" r="2" /></svg>);
}
function ScoutIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><circle cx="11" cy="11" r="3" /></svg>);
}
function AgentIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-3-3.87" /><path d="M4 21v-2a4 4 0 0 1 3-3.87" /><circle cx="12" cy="7" r="4" /><path d="M12 14l-3 3 3 3 3-3-3-3z" /></svg>);
}
function TeamIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C6 4 6 4.5 6 4.5" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5C18 4 18 4.5 18 4.5" /><path d="M4 22v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" /><circle cx="12" cy="11" r="4" /><path d="M12 2l2 4h-4l2-4z" /></svg>);
}
function MedicalIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" /><path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" /><circle cx="20" cy="10" r="2" /></svg>);
}
function CoachIcon({ size = 16, color = 'currentColor' }) {
    return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 16l10-7 10 7" /><path d="M12 9v13" /><circle cx="12" cy="5" r="3" /></svg>);
}

/* ─── Navigation groups ─── */
const NAV_GROUPS = [
    {
        id: 'core',
        label: 'Core',
        items: [
            { label: 'Matrix',   IconComponent: MatrixIcon,  path: '/matrix',   color: '#d4a017' },
            { label: 'Digital Twin', IconComponent: VaultIcon, path: '/digital-twin', color: '#b8860b' },
            { label: 'Athletes', IconComponent: MatrixIcon,  path: '/athletes', color: '#c49000' },
            { label: 'Rocket',   IconComponent: RocketIcon,  path: '/rocket',   color: '#edd714' },
            { label: 'Trust',    IconComponent: TrustIcon,   path: '/trust',    color: '#d4a017' },
        ],
    },
    {
        id: 'social',
        label: 'Social',
        items: [
            { label: 'Matrix',        IconComponent: MatrixIcon,        path: '/explore',       color: '#edd714' },
            { label: 'Messages',      IconComponent: MessagesIcon,      path: '/messages',      color: '#d4a017' },
            { label: 'Notifications', IconComponent: NotificationsIcon, path: '/notifications', color: '#c49000' },
            { label: 'Analytics',     IconComponent: AnalyticsIcon,     path: '/analytics',     color: '#b8860b' },
        ],
    },
    {
        id: 'tools',
        label: 'Tools',
        items: [
            { label: 'Twin Studio',   IconComponent: TwinStudioIcon,    path: '/twin-pipeline', color: '#b8860b' },
            // { label: 'Live Stream',   IconComponent: LiveStreamIcon,    path: '/live-stream',   color: '#c49000' }, // Hidden — re-enable when ready
            { label: 'Blast',         IconComponent: BlastIcon,         path: '/blast',         color: '#edd714' },
            { label: 'Outreach',      IconComponent: OutreachIcon,      path: '/outreach',      color: '#d4a017' },
            { label: 'Partners',  IconComponent: SponsorshipsIcon,  path: '/sponsorships',  color: '#b8860b' },
            { label: 'Connections',   IconComponent: ConnectionsIcon,   path: '/connections',   color: '#c49000' },
            { label: 'Tickets',      IconComponent: TicketsIcon,       path: '/my-tickets',    color: '#d4a017' },
            { label: 'Live Events',  IconComponent: LiveEventsIcon,    path: '/live-events',   color: '#edd714' },
            { label: 'Calendar',     IconComponent: CalendarIcon,      path: '/calendar',      color: '#edd714' },
            { label: 'Import Athletes', IconComponent: TeamIcon,       path: '/agency-import', color: '#c49000', visibleTo: ['agency'] },
        ],
    },
    {
        id: 'market',
        label: 'Market',
        items: [
            { label: 'Companies',     IconComponent: CompaniesIcon,     path: '/companies', color: '#d4a017' },
            { label: 'Teams',         IconComponent: TeamsCollectivesIcon, path: '/teams-collectives', color: '#b8860b' },
            { label: 'Marketplace',   IconComponent: MarketplaceIcon,   path: '/marketplace', color: '#c49000' },
            { label: 'Beacon',        IconComponent: BeaconIcon,        path: '/beacon', color: '#d4a017' },
            { label: 'Services',      IconComponent: ServiceMktIcon,    path: '/service-marketplace', color: '#c49000' },
            { label: 'Commerce',      IconComponent: CommerceIcon,      path: '/commerce-hub',        color: '#edd714' },
        ],
    },
];

const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 260;

const useStyles = makeStyles(() => ({
    '@keyframes glowPulse': {
        '0%, 100%': { opacity: 0.6 },
        '50%': { opacity: 1 },
    },

    /* ─── Sidebar root ─── */
    sidebar: {
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: COLLAPSED_WIDTH,
        background: 'rgba(6, 6, 10, 0.94)',
        backdropFilter: 'blur(40px) saturate(1.5)',
        WebkitBackdropFilter: 'blur(40px) saturate(1.5)',
        borderRight: '1px solid rgba(255,255,255,0.04)',
        boxShadow: '4px 0 48px rgba(0,0,0,0.4)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '&:hover': {
            width: EXPANDED_WIDTH,
        },
    },

    /* ─── Logo ─── */
    logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '18px 16px',
        textDecoration: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        minHeight: 68,
        flexShrink: 0,
        transition: 'background 0.2s ease',
        '&:hover': {
            background: 'rgba(237,215,20,0.03)',
        },
    },
    logoImg: {
        width: 38,
        height: 38,
        minWidth: 38,
        borderRadius: 10,
        objectFit: 'cover',
    },
    logoTextWrap: {
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },
    logoText: {
        fontFamily: "'Orbitron', sans-serif",
        fontWeight: 900,
        fontSize: 13,
        color: '#f0f0f5',
        letterSpacing: '1.5px',
        lineHeight: 1,
    },
    logoVersion: {
        fontFamily: "'Orbitron', sans-serif",
        fontWeight: 700,
        fontSize: 9,
        color: '#edd714',
        letterSpacing: '2px',
        lineHeight: 1,
        marginTop: 3,
    },

    /* ─── Search trigger ─── */
    searchTrigger: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '12px 12px 4px',
        padding: '9px 12px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.04)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            background: 'rgba(255,255,255,0.06)',
            borderColor: 'rgba(237,215,20,0.15)',
        },
    },
    searchLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontSize: 12,
        color: 'rgba(255,255,255,0.25)',
        flex: 1,
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },
    searchKbd: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1px 6px',
        borderRadius: 5,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.06)',
        fontFamily: "'SF Mono', monospace",
        fontSize: 10,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.2)',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },

    /* ─── Nav sections ─── */
    navScroll: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        padding: '4px 0',
    },
    navGroup: {
        marginBottom: 2,
    },
    groupHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 20px 4px',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover $groupLabel': { color: 'rgba(255,255,255,0.35)' },
    },
    groupLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 700,
        fontSize: 9,
        letterSpacing: '1.8px',
        color: 'rgba(255,255,255,0.18)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s, color 0.2s',
        '$sidebar:hover &': { opacity: 1 },
    },
    groupChevron: {
        color: 'rgba(255,255,255,0.15)',
        transition: 'transform 0.25s ease',
        opacity: 0,
        '$sidebar:hover &': { opacity: 1 },
    },
    groupChevronClosed: {
        transform: 'rotate(-90deg)',
    },
    groupItems: {
        overflow: 'hidden',
        transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
    },
    divider: {
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04) 20%, rgba(255,255,255,0.04) 80%, transparent)',
        margin: '4px 16px',
    },

    /* ─── Nav item ─── */
    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '8px 16px 8px 20px',
        textDecoration: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.15s ease',
        borderRadius: 0,
        minHeight: 40,
        '&:hover': {
            background: 'rgba(255,255,255,0.03)',
        },
    },
    navItemActive: {
        background: 'rgba(255,255,255,0.02)',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: '0 3px 3px 0',
            background: 'var(--active-color, #edd714)',
            boxShadow: '0 0 12px var(--active-color, #edd714)',
            animation: '$glowPulse 2.5s ease infinite',
        },
    },
    navIconWrap: {
        width: 30,
        height: 30,
        minWidth: 30,
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        background: 'transparent',
    },
    navIconWrapActive: {
        background: 'rgba(255,255,255,0.05)',
    },
    navLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 500,
        fontSize: 12.5,
        letterSpacing: '0.1px',
        color: 'rgba(255,255,255,0.45)',
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s, color 0.15s ease',
        '$sidebar:hover &': { opacity: 1 },
    },
    navLabelActive: {
        color: '#f0f0f5',
        fontWeight: 600,
    },

    /* ─── Bottom section ─── */
    bottomSection: {
        borderTop: '1px solid rgba(255,255,255,0.04)',
        padding: '8px 0 12px',
        flexShrink: 0,
    },
    profileRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '10px 16px 10px 20px',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': {
            background: 'rgba(255,255,255,0.03)',
        },
    },
    avatar: {
        width: 32,
        height: 32,
        minWidth: 32,
        fontSize: 13,
        fontWeight: 800,
        background: 'linear-gradient(135deg, #edd714 0%, #d4c212 100%)',
        color: '#0a0a0a',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 0 20px rgba(237,215,20,0.25)',
        },
    },
    profileInfo: {
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },
    profileName: {
        fontFamily: "'Exo 2', sans-serif",
        fontSize: 12,
        fontWeight: 600,
        color: '#f0f0f5',
        lineHeight: 1,
        marginBottom: 3,
    },
    tierBadge: {
        fontFamily: "'Exo 2', sans-serif",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '1px',
        padding: '2px 8px',
        borderRadius: 100,
        display: 'inline-block',
        textTransform: 'uppercase',
    },
    logoutRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '6px 16px 6px 20px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': {
            background: 'rgba(255,255,255,0.03)',
        },
    },
    logoutIconWrap: {
        width: 30,
        height: 30,
        minWidth: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    logoutLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 500,
        fontSize: 11,
        color: 'rgba(255,255,255,0.25)',
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s, color 0.15s ease',
        '$sidebar:hover &': { opacity: 1 },
        '&:hover': { color: '#fff' },
    },

    /* ─── Top-right auth ─── */
    topRightAuth: {
        position: 'fixed',
        top: 20,
        right: 28,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1301,
    },
    authBtn: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: '0.5px',
        padding: '9px 22px',
        borderRadius: 12,
        textDecoration: 'none',
        textAlign: 'center',
        transition: 'all 0.25s ease',
        display: 'inline-block',
        whiteSpace: 'nowrap',
    },
    signInBtn: {
        color: 'rgba(255,255,255,0.6)',
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(10,10,10,0.5)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        '&:hover': {
            borderColor: '#edd714',
            color: '#edd714',
        },
    },
    signUpBtn: {
        background: 'linear-gradient(135deg, #edd714 0%, #d4c212 100%)',
        color: '#0a0a0a',
        border: '1px solid transparent',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 24px rgba(237,215,20,0.3)',
        },
    },

    /* ─── Unauth sidebar CTA ─── */
    unauthCta: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '4px 12px 8px',
        padding: '10px 0',
        borderRadius: 10,
        background: 'linear-gradient(135deg, #edd714 0%, #d4c212 100%)',
        textDecoration: 'none',
        transition: 'all 0.25s ease',
        overflow: 'hidden',
        minHeight: 36,
        '&:hover': {
            boxShadow: '0 4px 18px rgba(237,215,20,0.35)',
            transform: 'translateY(-1px)',
        },
    },
    unauthCtaLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '1px',
        color: '#0a0a0a',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },

    /* ─── Unauth "Join As" user groups ─── */
    joinAsSection: {
        padding: '8px 12px 4px',
    },
    joinAsLabel: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 700,
        fontSize: 9,
        letterSpacing: '1.8px',
        color: 'rgba(255,255,255,0.18)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        padding: '4px 8px 8px',
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },
    joinAsGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        opacity: 0,
        transition: 'opacity 0.2s ease 0.08s',
        '$sidebar:hover &': { opacity: 1 },
    },
    joinAsChip: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 10px',
        borderRadius: 8,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            background: 'rgba(237,215,20,0.06)',
        },
    },
    joinAsEmoji: {
        fontSize: 14,
        width: 20,
        textAlign: 'center',
        flexShrink: 0,
    },
    joinAsName: {
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 500,
        fontSize: 11.5,
        color: 'rgba(255,255,255,0.45)',
        whiteSpace: 'nowrap',
        transition: 'color 0.15s ease',
        '$joinAsChip:hover &': {
            color: '#edd714',
        },
    },
    /* ─── Collapsed icon-only state for user groups ─── */
    joinAsIconOnly: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '8px 0',
        '$sidebar:hover &': { display: 'none' },
    },
    joinAsIconDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: 'rgba(237,215,20,0.25)',
    },
}));

export default function Sidebar(_props) {
    const { loading, data } = useCurrentUser();
    const classes = useStyles();
    const firebase = useFirebase();
    const history = useHistory();
    const location = useLocation();
    const { tierLabel, isAtLeast } = useFeatureAccess();

    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [unreadMsgCount, setUnreadMsgCount] = useState(0);

    /* ─── Live unread message count from dealMessages ─── */
    useEffect(() => {
        const uid = firebase?.auth?.currentUser?.uid;
        if (!uid || !firebase?.db) return;
        const unsub = firebase.db
            .collection('dealMessages').doc(uid)
            .collection('threads')
            .where('unread', '==', true)
            .onSnapshot(
                snap => setUnreadMsgCount(snap.size),
                () => {},
            );
        return unsub;
    }, [firebase, data]);

    const toggleGroup = (id) => {
        setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleLogout = () => {
        firebase.logout();
        history.push('/login');
    };

    const getInitials = () => {
        if (!data) return '?';
        if (data.companyName) return data.companyName.charAt(0).toUpperCase();
        const email = data.email || '';
        return email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!data) return '';
        if (data.companyName) return data.companyName;
        if (data.firstName) return `${data.firstName} ${data.lastName || ''}`.trim();
        return data.email || '';
    };

    const getTierStyle = () => {
        if (!data) return {};
        const isElite = isAtLeast(data.type === 'athlete' ? 'athlete_elite' : 'employer_enterprise');
        const isPro = isAtLeast(data.type === 'athlete' ? 'athlete_pro' : 'employer_business');
        if (isElite) return { backgroundColor: 'rgba(237,215,20,0.1)', color: '#edd714', border: '1px solid rgba(237,215,20,0.2)' };
        if (isPro) return { backgroundColor: 'rgba(196,144,0,0.06)', color: '#c49000', border: '1px solid rgba(196,144,0,0.15)' };
        return { backgroundColor: 'rgba(255,255,255,0.04)', color: '#666', border: '1px solid rgba(255,255,255,0.06)' };
    };

    const renderNavItem = (item, isActive) => {
        const IconComp = item.IconComponent;
        const isModule = NAV_GROUPS[0].items.includes(item);
        const linkPath = (isModule && !data) ? '/features' : item.path;
        const isMessages = item.path === '/messages';
        const showBadge = isMessages && unreadMsgCount > 0;

        return (
            <Link
                key={item.path}
                to={linkPath}
                className={`${classes.navItem} ${isActive ? classes.navItemActive : ''}`}
                style={isActive ? { '--active-color': item.color } : undefined}
            >
                {/* Icon with unread dot in collapsed state */}
                <div className={`${classes.navIconWrap} ${isActive ? classes.navIconWrapActive : ''}`}
                     style={{ position: 'relative' }}>
                    <IconComp size={18} color={isActive ? item.color : 'rgba(255,255,255,0.35)'} />
                    {showBadge && (
                        <span style={{
                            position: 'absolute', top: 2, right: 2,
                            width: 7, height: 7, borderRadius: '50%',
                            background: '#edd714',
                            boxShadow: '0 0 6px rgba(237,215,20,0.7)',
                            border: '1px solid rgba(6,6,10,0.8)',
                        }} />
                    )}
                </div>
                {/* Label with count badge in expanded state */}
                <span className={`${classes.navLabel} ${isActive ? classes.navLabelActive : ''}`}
                      style={isActive ? { color: item.color } : undefined}>
                    {item.label}
                    {showBadge && (
                        <span style={{
                            marginLeft: 6,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: 16, height: 16, borderRadius: 100,
                            background: '#edd714', color: '#0a0a0a',
                            fontFamily: "'Exo 2', sans-serif",
                            fontSize: 9, fontWeight: 800,
                            padding: '0 4px',
                        }}>
                            {unreadMsgCount > 99 ? '99+' : unreadMsgCount}
                        </span>
                    )}
                </span>
            </Link>
        );
    };

    /* ─── Minimal unauth sidebar items ─── */
    // eslint-disable-next-line no-unused-vars
    const UNAUTH_ITEMS = [
        { label: 'Features',  IconComponent: ExploreIcon,     path: '/features', color: '#edd714' },
        { label: 'Pricing',   IconComponent: MarketplaceIcon, path: '/store',    color: '#c49000' },
    ];

    /* ─── All user groups that can sign up ─── */
    // eslint-disable-next-line no-unused-vars
    const USER_GROUPS = [
        { value: 'athlete',           IconComponent: AthleteIcon,    label: 'Athlete' },
        { value: 'employer',          IconComponent: CompanyIcon,    label: 'Company' },
        { value: 'collective',        IconComponent: CollectiveIcon, label: 'Collective' },
        { value: 'agency',            IconComponent: AgencyIcon,     label: 'Agency' },
        { value: 'trainer',           IconComponent: TrainerIcon,    label: 'Trainer' },
        { value: 'scout',             IconComponent: ScoutIcon,      label: 'Scout' },
        { value: 'individual_agent',  IconComponent: AgentIcon,      label: 'Agent' },
        { value: 'team',              IconComponent: TeamIcon,       label: 'Team' },
        { value: 'medical_services',  IconComponent: MedicalIcon,    label: 'Medical' },
        { value: 'coach',             IconComponent: CoachIcon,      label: 'Coach' },
    ];

    const isAuthenticated = !loading && !!data;
    const isUnauthenticated = !loading && !data;

    return (
        <>
        <div className={classes.sidebar}>
            {/* Logo */}
            <Link to="/" className={classes.logoSection}>
                <img className={classes.logoImg} src={logo} alt="Athletes Launch" />
                <div className={classes.logoTextWrap}>
                    <div className={classes.logoText}>ATHLETES LAUNCH</div>
                    <div className={classes.logoVersion}>2.0</div>
                </div>
            </Link>

            {/* Search trigger — auth only */}
            {isAuthenticated && (
                <div
                    className={classes.searchTrigger}
                    onClick={() => {
                        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
                    }}
                >
                    <SearchIcon size={14} color="rgba(255,255,255,0.2)" />
                    <span className={classes.searchLabel}>Search…</span>
                    <span className={classes.searchKbd}>⌘K</span>
                </div>
            )}

            {/* Scrollable nav */}
            <div className={classes.navScroll}>
                {isAuthenticated && (() => {
                    const userType = data?.type || '';
                    return NAV_GROUPS.map((group, gi) => {
                        const isCollapsed = collapsedGroups[group.id];
                        const visibleItems = group.items.filter(item =>
                            !item.visibleTo || item.visibleTo.includes(userType)
                        );
                        if (visibleItems.length === 0) return null;
                        return (
                            <div key={group.id} className={classes.navGroup}>
                                {gi > 0 && <div className={classes.divider} />}
                                <div className={classes.groupHeader} onClick={() => toggleGroup(group.id)}>
                                    <span className={classes.groupLabel}>{group.label}</span>
                                    <ChevronDown
                                        size={12}
                                        style={{
                                            color: 'rgba(255,255,255,0.15)',
                                            transition: 'transform 0.25s ease, opacity 0.2s ease 0.08s',
                                            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                            opacity: 0,
                                        }}
                                        className={classes.groupChevron}
                                    />
                                </div>
                                <div
                                    className={classes.groupItems}
                                    style={{
                                        maxHeight: isCollapsed ? 0 : `${visibleItems.length * 44}px`,
                                        opacity: isCollapsed ? 0 : 1,
                                    }}
                                >
                                    {visibleItems.map(item => {
                                        const isActive = location.pathname.startsWith(item.path)
                                            || location.pathname === `${item.path}-landing`;
                                        return renderNavItem(item, isActive);
                                    })}
                                </div>
                            </div>
                        );
                    });
                })()}

                {/* Unauth: minimal nav — Sign In only */}
            </div>

            {/* Bottom: profile + logout (auth) */}
            {isAuthenticated && (
                <div className={classes.bottomSection}>
                    <Link to="/profile" className={classes.profileRow} style={{ textDecoration: 'none' }}>
                        <Avatar className={classes.avatar}>{getInitials()}</Avatar>
                        <div className={classes.profileInfo}>
                            <div className={classes.profileName}>{getDisplayName()}</div>
                            <span className={classes.tierBadge} style={getTierStyle()}>
                                {tierLabel}
                            </span>
                        </div>
                    </Link>
                    <div className={classes.logoutRow} onClick={handleLogout}>
                        <div className={classes.logoutIconWrap}>
                            <LogoutIcon size={16} color="rgba(255,255,255,0.2)" />
                        </div>
                        <span className={classes.logoutLabel}>Sign Out</span>
                    </div>
                </div>
            )}

            {/* Bottom: Sign In (unauth) */}
            {isUnauthenticated && (
                <div className={classes.bottomSection}>
                    <Link to="/login" className={classes.profileRow} style={{ textDecoration: 'none' }}>
                        <div className={classes.navIconWrap}>
                            <LogoutIcon size={16} color="rgba(255,255,255,0.45)" />
                        </div>
                        <span className={classes.navLabel}>Sign In</span>
                    </Link>
                </div>
            )}
        </div>

        {/* Top-right auth button (logged out only) */}
        {isUnauthenticated && (
            <div className={classes.topRightAuth}>
                <Link to="/login" className={`${classes.authBtn} ${classes.signInBtn}`}>Sign In</Link>
            </div>
        )}
        </>
    );
}

export { COLLAPSED_WIDTH, EXPANDED_WIDTH };
